import type {SelectItemProps} from '@smart/react-components';
import React, {useMemo} from 'react';
import type {Maybe} from '../../graphql';
import type {availableCountries} from '../common/types';
import {availableContactLanguages} from '../common/types';
import {languageMap} from '../common/types';
import {useTranslations} from '../hooks/use-translations';
import {useSchemas} from '../hooks/user-schemas';
import Fields from './easy-form/fields';

interface IProps {
  tag: string;
  mkt?: (typeof availableCountries)[number] | 'ALL';
  required?: boolean;
  label?: Maybe<string>;
  initial?: string;
}

export function MktLangSelect({
  initial,
  tag,
  mkt,
  required: required = false,
  label,
}: IProps): JSX.Element {
  const translate = useTranslations();
  const generalSchemas = useSchemas();
  const mktLangs = mkt
    ? mkt === `ALL`
      ? availableContactLanguages
      : languageMap[mkt]
    : [];

  const translateLanguages = useMemo<SelectItemProps[]>(() => {
    let options: SelectItemProps[] = [
      ...(mktLangs as string[]).map((langCode) => ({
        value: langCode,
        label: translate(`languages.${langCode}`),
      })),
    ].sort((a, b) => (a.label > b.label ? 1 : -1));

    if (mktLangs.length > 1) options = [{value: ``, label: ``}, ...options];

    return options;
  }, [translate, mkt]);

  const schema = useMemo(
    () => generalSchemas.oneOf(mktLangs as string[], required),
    [translateLanguages, required],
  );

  const isDisabled = translateLanguages.length === 1 || !mkt;

  return (
    <Fields.Select
      initial={initial}
      label={label}
      tag={tag}
      options={translateLanguages}
      required={required}
      schema={schema}
      props={{disabled: isDisabled}}
    />
  );
}
